import React from 'react';

/**
 * ChapterIndicator — Sticky documentary narrative chapter header.
 * Displays subtle chapter index, title, and current scroll progress.
 */
const ChapterIndicator = ({
  chapterIndex = 1,
  totalChapters = 12,
  chapterTitle = 'THE BUILDER',
  progress = 0.08, // 0 to 1
  className = ''
}) => {
  const formattedIndex = String(chapterIndex).padStart(2, '0');
  const formattedTotal = String(totalChapters).padStart(2, '0');
  const progressPercent = Math.min(100, Math.max(0, progress * 100));

  return (
    <div className={`chapter-indicator-bar ${className}`} role="region" aria-label="Story Chapter Progress">
      <div className="chapter-indicator-content">
        <div className="chapter-meta">
          <span className="chapter-num font-mono">CH. {formattedIndex} / {formattedTotal}</span>
          <span className="chapter-divider">—</span>
          <span className="chapter-title font-heading">{chapterTitle}</span>
        </div>
        <div className="chapter-progress-track">
          <div
            className="chapter-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <style jsx>{`
        .chapter-indicator-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: calc(var(--z-nav, 1000) - 1);
          background: transparent;
          backdrop-filter: none;
          pointer-events: none;
        }

        .chapter-indicator-content {
          max-width: 100%;
          margin: 0 auto;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .chapter-meta {
          display: none; /* Progress shown as thin bottom line only */
        }

        .chapter-num {
          color: var(--accent-gold);
          font-weight: 600;
          letter-spacing: 0.1em;
        }

        .chapter-divider {
          color: var(--text-dim);
        }

        .chapter-title {
          color: var(--text-main);
          font-size: var(--font-size-body-sm);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          font-weight: 600;
        }

        .chapter-progress-track {
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.08);
          overflow: hidden;
        }

        .chapter-progress-fill {
          height: 100%;
          background: var(--accent-gold);
          box-shadow: 0 0 8px rgba(212, 163, 89, 0.6);
          transition: width 0.2s ease-out;
        }

        @media (max-width: 768px) {
          /* On small screens keep progress bar visible but hide text meta */
          .chapter-meta {
            display: none !important;
          }
          .chapter-progress-track {
            height: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default ChapterIndicator;
